import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  CalendarDays,
  CircleDollarSign,
  Package,
  ReceiptIndianRupee,
  ShieldCheck,
  ShoppingBag,
  Target,
} from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import {
  getMilestoneCommissionDetail,
  clearMilestoneCommissionDetailState,
} from "../../../../Redux/referral/slice";
import {
  formatEarningsCurrency,
  formatEarningsDate,
  mapMilestoneCommissionItem,
  normalizeEarningsStatus,
  statusLabel,
} from "../earningsAdapters";
import "./MilestoneIncentives.css";

const statusClass = (status: string) =>
  `milestone-status milestone-status-${normalizeEarningsStatus(status || "")}`;

const ShoppingAmountDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams<{ id: string }>();
  const { data, loading, error } = useSelector(
    (state: RootState) => state.referral.milestoneCommissionDetail,
  );

  useEffect(() => {
    if (id) {
      dispatch(getMilestoneCommissionDetail(id));
    }
    return () => {
      dispatch(clearMilestoneCommissionDetailState());
    };
  }, [dispatch, id]);

  const backButton = (
    <button
      type="button"
      className="milestone-back-button"
      onClick={() => navigate("/ShoppingAmount")}
      aria-label="Back to milestone incentives"
    >
      <ArrowLeft size={20} />
    </button>
  );

  if (loading) {
    return (
      <section className="milestone-page">
        <div className="milestone-shell">
          <header className="milestone-page-header milestone-details-header">
            <div className="milestone-title-wrap">
              {backButton}
              <div>
                <span className="milestone-eyebrow">REWARD DETAILS</span>
                <h1>Milestone Incentive Details</h1>
                <p>Loading record...</p>
              </div>
            </div>
          </header>
        </div>
      </section>
    );
  }

  if (!data || error) {
    return (
      <section className="milestone-page">
        <div className="milestone-shell">
          <header className="milestone-page-header milestone-details-header">
            <div className="milestone-title-wrap">
              {backButton}
              <div>
                <span className="milestone-eyebrow">REWARD DETAILS</span>
                <h1>Milestone Incentive Details</h1>
                <p>
                  Order, product, achieved milestone and qualifying shopping
                  amount.
                </p>
              </div>
            </div>
          </header>
          <div className="milestone-empty">
            <Target size={30} />
            <strong>Record not found</strong>
            <span>{error || "This milestone record is unavailable."}</span>
          </div>
        </div>
      </section>
    );
  }

  const record = mapMilestoneCommissionItem(data);

  return (
    <section className="milestone-page">
      <div className="milestone-shell">
        <header className="milestone-page-header milestone-details-header">
          <div className="milestone-title-wrap">
            {backButton}
            <div>
              <span className="milestone-eyebrow">REWARD DETAILS</span>
              <h1>Milestone Incentive Details</h1>
              <p>
                Order, product, achieved milestone and qualifying shopping
                amount.
              </p>
            </div>
          </div>
          <span className={statusClass(record.status)}>
            {statusLabel(record.status)}
          </span>
        </header>

        <div className="milestone-details-grid">
          <section className="milestone-detail-card milestone-detail-order">
            <div className="milestone-detail-heading">
              <CalendarDays size={20} />
              <div>
                <span>ORDER INFORMATION</span>
                <h2>Purchase Record</h2>
              </div>
            </div>
            <div className="milestone-detail-kv-grid milestone-detail-kv-six">
              <div>
                <span>Order Date</span>
                <strong>{formatEarningsDate(record.orderDate || record.date)}</strong>
              </div>
              <div>
                <span>Order No</span>
                <strong>{record.orderId || "—"}</strong>
              </div>
              <div>
                <span>Product Type</span>
                <strong>{record.orderType || "—"}</strong>
              </div>
              <div>
                <span>Achieved Date</span>
                <strong>{formatEarningsDate(record.achievedDate)}</strong>
              </div>
              <div>
                <span>Stage</span>
                <strong>{record.milestoneName}</strong>
              </div>
              <div>
                <span>Status</span>
                <span className={statusClass(record.status)}>
                  {statusLabel(record.status)}
                </span>
              </div>
            </div>
          </section>

          <section className="milestone-detail-card">
            <div className="milestone-detail-heading">
              <Package size={20} />
              <div>
                <span>PRODUCT INFORMATION</span>
                <h2>Salar Shopping Voucher</h2>
              </div>
            </div>
            <div className="milestone-detail-product-row">
              <img
                src={record.productImage || undefined}
                alt=""
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
              <div>
                <span className="milestone-section-label">
                  PRODUCT NAME
                </span>
                <h3>{record.productName || "—"}</h3>
                <p>
                  Quantity:<strong> {record.quantity} Item</strong>
                </p>
                <strong className="milestone-detail-price">
                  {formatEarningsCurrency(record.grandTotal)}
                </strong>
              </div>
            </div>
          </section>

          <section className="milestone-detail-card">
            <div className="milestone-detail-heading">
              <Target size={20} />
              <div>
                <span>MILESTONE INFORMATION</span>
                <h2>Achieved Stage</h2>
              </div>
            </div>
            <div className="milestone-detail-highlight">
              <span>MILESTONE</span>
              <strong>{record.milestoneName}</strong>
              <small>
                Qualifying purchase successfully mapped to this stage.
              </small>
            </div>
          </section>

          <section className="milestone-detail-card">
            <div className="milestone-detail-heading">
              <ReceiptIndianRupee size={20} />
              <div>
                <span>VALUE BREAKDOWN</span>
                <h2>Purchase Value</h2>
              </div>
            </div>
            <div className="milestone-value-grid">
              <div>
                <span>Voucher Value</span>
                <strong>{formatEarningsCurrency(record.voucherAmount)}</strong>
              </div>
              <div>
                <span>Grand Total</span>
                <strong>{formatEarningsCurrency(record.grandTotal)}</strong>
              </div>
            </div>
            <div className="milestone-shopping-strip">
              <CircleDollarSign size={19} />
              <div>
                <span>Shopping Amount</span>
                <strong>{formatEarningsCurrency(record.grandTotal)}</strong>
              </div>
            </div>
          </section>

          <section className="milestone-detail-card">
            <div className="milestone-detail-heading">
              <ShoppingBag size={20} />
              <div>
                <span>PURCHASE TYPE</span>
                <h2>{record.orderType || "—"}</h2>
              </div>
            </div>
            <div className="milestone-detail-text">
              <strong>{record.productName || "Salar Shopping Voucher"}</strong>
              <p>
                Eligible purchase recorded under {record.milestoneName} with
                the qualifying shopping amount credited to this milestone.
              </p>
            </div>
          </section>
        </div>

        <section className="milestone-detail-footer-card">
          <ShieldCheck size={20} />
          <div>
            <strong>Milestone record protected</strong>
            <span>
              This incentive history is linked to your Salar account and
              order records.
            </span>
          </div>
        </section>
      </div>
    </section>
  );
};

export default ShoppingAmountDetail;
